import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useClerk } from "@clerk/react";
import { toast } from "sonner";
import { Smartphone } from "lucide-react";
import { getAuth, RecaptchaVerifier, signInWithPhoneNumber } from "firebase/auth";
import { publicApi, errMsg } from "@/lib/api";
import { firebaseEnabled, getFirebase } from "@/lib/firebase";

const input = "w-full rounded-xl border border-hc-sage bg-white px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-hc-green";
const btn = "w-full rounded-xl bg-hc-green py-3 text-sm font-semibold !text-white transition-colors duration-200 hover:bg-hc-deep disabled:opacity-50";
const webFinish = (ticket, isNew) => {
  const back = new URLSearchParams(window.location.search).get("redirect_url");
  const target = isNew ? `${window.location.origin}/verify?welcome=1` : back;
  const next = target ? `&redirect_url=${encodeURIComponent(target)}` : "";
  window.location.href = `/sign-in?__clerk_ticket=${encodeURIComponent(ticket)}${next}`;
};

const fmtDob = (v) => {
  const d = v.replace(/\D/g, "").slice(0, 8);
  return [d.slice(0, 2), d.slice(2, 4), d.slice(4)].filter(Boolean).join("/");
};
const dobIso = (v) => {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(v);
  if (!m) return "";
  const [, dd, mm, yyyy] = m;
  const dt = new Date(Date.UTC(+yyyy, +mm - 1, +dd));
  const ok = dt.getUTCDate() === +dd && dt.getUTCMonth() === +mm - 1 && +yyyy >= 1900 && dt < new Date();
  return ok ? `${yyyy}-${mm}-${dd}` : "";
};

function SignupForm({ signup, finish, app }) {
  const [f, setF] = useState({ name: "", email: "", dob: "", code: "" });
  const [sent, setSent] = useState(false);
  const [needCode, setNeedCode] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));
  const run = async (fn) => { setBusy(true); try { await fn(); } catch (e) { toast.error(errMsg(e)); } setBusy(false); };
  const sendCode = async () => { await publicApi.post("/auth/phone/email-otp", { signup_token: signup.signup_token, email: f.email }); setSent(true); toast.success(`Code sent to ${f.email}`); };
  const resend = () => run(sendCode);
  const complete = (code = f.code) => run(async () => {
    try {
      const r = await publicApi.post("/auth/phone/complete", { signup_token: signup.signup_token, email: f.email, name: f.name, dob: dobIso(f.dob), email_code: code, handoff: !!app });
      if (!r.data.email_verified) toast.success(`We've sent a verification link to ${f.email}`);
      await finish(r.data.code || r.data.ticket, true);
    } catch (e) {
      if (e?.response?.data?.detail !== "EMAIL_CODE_REQUIRED") throw e;
      setNeedCode(true);
      await sendCode();
    }
  });
  const ready = f.name.trim().length >= 2 && /^\S+@\S+\.\S+$/.test(f.email) && !!dobIso(f.dob);
  return (
    <div className="space-y-3" data-testid="phone-signup-form">
      <p className="text-sm text-hc-slate">+91 {signup.phone} is verified. New here? Create your account — next you'll upload your driving licence and Aadhaar for verification.</p>
      <input className={input} placeholder="Full name (as on licence)" value={f.name} onChange={set("name")} data-testid="phone-signup-name" />
      <input className={input} type="email" placeholder="Email address" value={f.email} onChange={set("email")} disabled={needCode} data-testid="phone-signup-email" />
      <div><p className="mb-1 text-xs text-neutral-500">Date of birth</p>
        <input className={`${input} tabular`} inputMode="numeric" autoComplete="bday" placeholder="DD/MM/YYYY" value={f.dob} onChange={(e) => setF((p) => ({ ...p, dob: fmtDob(e.target.value) }))} data-testid="phone-signup-dob" />
        {f.dob.length === 10 && !dobIso(f.dob) && <p className="mt-1 text-xs text-rose-600" data-testid="phone-signup-dob-error">Enter a valid date as DD/MM/YYYY</p>}</div>
      {!needCode ? (
        <>
          <button className={btn} disabled={busy || !ready} onClick={() => complete("")} data-testid="phone-signup-complete">{busy ? "Creating your account…" : "Create account"}</button>
          <p className="text-xs text-hc-slate">We'll email you a link to confirm your address. You can keep going while you wait.</p>
        </>
      ) : (
        <>
          <p className="text-xs text-hc-slate" data-testid="phone-signup-code-reason">This email already has a Hoorecars account. Enter the 6-digit code we sent to {f.email} to link it.</p>
          <input className={`${input} font-mono tracking-[0.4em]`} inputMode="numeric" autoComplete="one-time-code" autoFocus maxLength={6} placeholder="6-digit email code" value={f.code} onChange={(e) => { const v = e.target.value.replace(/\D/g, "").slice(0, 6); setF((p) => ({ ...p, code: v })); if (v.length === 6 && !busy) complete(v); }} data-testid="phone-signup-email-code" />
          <button className={btn} disabled={busy || f.code.length !== 6} onClick={() => complete()} data-testid="phone-signup-code-submit">{busy ? "Checking…" : "Continue"}</button>
          <button className="w-full text-xs font-semibold text-hc-green" onClick={resend} disabled={busy} data-testid="phone-signup-code-resend">Resend email code</button>
        </>
      )}
    </div>
  );
}

const errText = {
  "auth/too-many-requests": "Too many attempts. Try again later.",
  "auth/invalid-phone-number": "That mobile number doesn't look right.",
  "auth/quota-exceeded": "We've hit today's SMS limit. Please try again later or contact support.",
  "auth/captcha-check-failed": "Security check failed. Refresh the page and try again.",
  "auth/unauthorized-domain": "Mobile sign-in isn't enabled for this website address yet.",
  "auth/operation-not-allowed": "Mobile sign-in is switched off right now.",
  "auth/network-request-failed": "Network problem. Check your internet and try again.",
};

function useInPlaceSignIn() {
  const clerk = useClerk();
  const navigate = useNavigate();
  return async (ticket, isNew) => {
    const back = new URLSearchParams(window.location.search).get("redirect_url");
    let path = "/";
    try {
      const u = new URL(back || "/", window.location.origin);
      if (u.origin === window.location.origin) path = u.pathname + u.search + u.hash;
    } catch { /* keep home */ }
    try {
      const si = await clerk.client.signIn.create({ strategy: "ticket", ticket });
      await clerk.setActive({ session: si.createdSessionId });
      navigate(isNew ? "/verify?welcome=1" : path, { replace: true });
    } catch (e) {
      console.warn("In-place sign-in failed, using redirect", e);
      webFinish(ticket, isNew);
    }
  };
}

export function PhoneLogin({ onTicket, footer = "", app = false }) {
  const inPlace = useInPlaceSignIn();
  const finish = onTicket || inPlace;
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [confirm, setConfirm] = useState(null);
  const [signup, setSignup] = useState(null);
  const [busy, setBusy] = useState("");
  const [capKey, setCapKey] = useState(0);
  const verifier = useRef(null);
  const inFlight = useRef(false);
  useEffect(() => {
    if (!firebaseEnabled) return;
    try {
      verifier.current = new RecaptchaVerifier(getAuth(getFirebase()), `phone-recaptcha-${capKey}`, { size: "invisible" });
      verifier.current.render().catch(() => {});
    } catch { verifier.current = null; }
    return () => { verifier.current?.clear(); verifier.current = null; };
  }, [capKey]);
  if (!firebaseEnabled) return null;

  const send = async () => {
    setBusy("send");
    try {
      const auth = getAuth(getFirebase());
      verifier.current = verifier.current || new RecaptchaVerifier(auth, `phone-recaptcha-${capKey}`, { size: "invisible" });
      const timeout = new Promise((_, rej) => setTimeout(() => rej({ code: "hc/timeout" }), 90000));
      setConfirm(await Promise.race([signInWithPhoneNumber(auth, `+91${phone}`, verifier.current), timeout]));
      toast.success(`OTP sent to +91 ${phone}`);
    } catch (e) {
      toast.error(e?.code === "hc/timeout" ? "The security check wasn't completed. Tap Send OTP to try again." : errText[e?.code] || `Couldn't send the OTP (${e?.code || "unknown error"}). Please try again.`);
      setCapKey((k) => k + 1);
    }
    setBusy("");
  };
  const verify = async (otp = code) => {
    if (busy || inFlight.current) return;
    inFlight.current = true;
    setBusy("verify");
    try {
      const cred = await confirm.confirm(otp);
      const r = await publicApi.post("/auth/phone/start", { firebase_id_token: await cred.user.getIdToken(), handoff: !!app });
      if (r.data.status === "signed_in") {
        toast.dismiss();
        setBusy("signin");
        await finish(r.data.code || r.data.ticket, false);
      } else setSignup(r.data);
    } catch (e) { toast.error(e?.code === "auth/invalid-verification-code" ? "Incorrect OTP" : errMsg(e)); }
    inFlight.current = false;
    setBusy("");
  };
  const onCode = (e) => {
    const v = e.target.value.replace(/\D/g, "").slice(0, 6);
    setCode(v);
    if (v.length === 6) verify(v);
  };

  return (
    <div className="w-full max-w-[400px] rounded-2xl border border-hc-sage bg-white p-6 shadow-sm" data-testid="phone-login">
      <p className="flex items-center gap-2 font-semibold text-hc-ink"><Smartphone className="h-4 w-4 text-hc-green" />Continue with mobile number</p>
      <div className="mt-4 space-y-3">
        {signup ? <SignupForm signup={signup} finish={finish} app={app} /> : !confirm ? (
          <>
            <div className="flex gap-2"><span className="flex items-center rounded-xl border border-hc-sage px-3 text-sm">+91</span>
              <input className={input} inputMode="numeric" autoComplete="tel-national" placeholder="10-digit mobile number" value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").replace(/^(91|0)(?=\d{10}$)/, "").slice(0, 10))} data-testid="phone-login-number" /></div>
            <button className={btn} disabled={!!busy || phone.length !== 10} onClick={send} data-testid="phone-login-send">{busy ? "Sending OTP…" : "Send OTP"}</button>
          </>
        ) : (
          <>
            <input className={`${input} font-mono tracking-[0.4em]`} inputMode="numeric" autoComplete="one-time-code" autoFocus maxLength={6} placeholder="6-digit OTP" value={code} onChange={onCode} data-testid="phone-login-code" />
            <button className={btn} disabled={!!busy || code.length !== 6} onClick={() => verify()} data-testid="phone-login-verify">{busy === "signin" ? "Signing you in…" : busy ? "Verifying…" : "Verify & continue"}</button>
            <button className="w-full text-xs font-semibold text-neutral-500" onClick={() => { setConfirm(null); setCode(""); }}>Change number</button>
          </>
        )}
      </div>
      <div key={capKey} id={`phone-recaptcha-${capKey}`} />
      {footer && <p className="mt-4 text-center text-xs text-hc-slate">{footer}</p>}
    </div>
  );
}
